import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Mail, Phone, Shield, Clock, CheckCircle, 
  XCircle, Key, Trash2, MessageSquare, ClipboardList, Edit, History, Calendar, Filter, MapPin, Layers, Play, Square, CheckSquare, Printer, MapPinCheck, LogIn, LogOut as LogOutIcon, Send, Trophy, BarChart3, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Sidebar from '../components/dashboard/Sidebar';
import AuthContext from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';
import TaskComments from '../components/TaskComments';
import usePlanLimits from '../hooks/usePlanLimits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CrewManagement = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const { hasFeature } = useFeatures();
  const { checkCanAdd, getLimitInfo } = usePlanLimits();
  const [crewMembers, setCrewMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [hives, setHives] = useState([]);
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [historyDateFilter, setHistoryDateFilter] = useState('all'); // 'all', '7days', '30days', '90days'
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'crew', 'yard'
  const [filterPriority, setFilterPriority] = useState('all'); // 'all', 'urgent', 'high', 'normal', 'low'
  const [filterDue, setFilterDue] = useState('all'); // 'all', 'overdue', 'today', 'week', 'no_date'
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printCrewFilter, setPrintCrewFilter] = useState('all');
  const [printLoading, setPrintLoading] = useState(false);
  const [crewCheckins, setCrewCheckins] = useState([]);
  const [timeMetrics, setTimeMetrics] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [autoEmailEnabled, setAutoEmailEnabled] = useState(true);
  const [reportHistory, setReportHistory] = useState([]);
  const [showReportHistory, setShowReportHistory] = useState(false);
  const [selectedHistoryReport, setSelectedHistoryReport] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTaskId, setAssignTaskId] = useState(null);
  const [assignCrewIds, setAssignCrewIds] = useState([]);
  
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    phone: '',
    experience_level: '',
    password: '',
    role: 'crew'
  });
  
  const [newTask, setNewTask] = useState({
    assigned_crew: [], // Multiple crew members
    selected_yard: '', // Yard selection for hive filtering
    hive_selection: 'single', // 'single', 'all'
    hive_id: '',
    hive_number: 0,
    yard: '',
    task_type: '',
    title: '',
    description: '',
    priority: 'normal',
    due_date: ''
  });
  
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token && localStorage.getItem('demoMode') === 'true') {
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      
      const [membersRes, tasksRes, completedTasksRes, hivesRes, yardsRes, checkinsRes, metricsRes, reportRes, prefRes, historyRes] = await Promise.all([
        axios.get(`${API}/crew/members`, { headers }),
        axios.get(`${API}/crew/tasks`, { headers }),
        axios.get(`${API}/crew/tasks?status=completed`, { headers }),
        axios.get(`${API}/hives`, { headers }),
        axios.get(`${API}/yards`, { headers }),
        axios.get(`${API}/crew/check-ins/all?days=7`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/crew/time-metrics?days=30`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/crew/weekly-report`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/auth/weekly-report-preference`, { headers }).catch(() => ({ data: { weekly_report_auto_email: true } })),
        axios.get(`${API}/crew/reports/history`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setCrewMembers(membersRes.data || []);
      setTasks((tasksRes.data || []).filter(t => t.status !== 'completed'));
      setCompletedTasks(completedTasksRes.data || []);
      setHives(hivesRes.data || []);
      setYards(yardsRes.data || []);
      setCrewCheckins(checkinsRes.data || []);
      setTimeMetrics(metricsRes.data);
      setWeeklyReport(reportRes.data);
      setAutoEmailEnabled(prefRes.data?.weekly_report_auto_email !== false);
      setReportHistory(historyRes.data || []);
    } catch (error) {
      toast.error('Failed to load crew data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/crew/invite`, newMember, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Crew member "${newMember.name}" added!`);
      setShowAddDialog(false);
      setNewMember({ name: '', email: '', phone: '', experience_level: '', password: '', role: 'crew' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add crew member');
    }
  };

  const handleCreateTask = async () => {
    if (newTask.assigned_crew.length === 0 || !newTask.title) {
      toast.error('Please select at least one crew member and enter a task title');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Determine which hives to create tasks for
      let targetHives = [];
      if (newTask.hive_selection === 'all' && newTask.selected_yard) {
        // Get all hives in the selected yard
        targetHives = hives.filter(h => h.yard === newTask.selected_yard);
      } else if (newTask.hive_id) {
        // Single hive selected
        const hive = hives.find(h => h.id === newTask.hive_id);
        if (hive) targetHives = [hive];
      }
      
      // If no hives, create a single task without hive assignment
      if (targetHives.length === 0) {
        await axios.post(`${API}/crew/tasks`, {
          assigned_crew: newTask.assigned_crew,
          title: newTask.title,
          description: newTask.description,
          task_type: newTask.task_type,
          priority: newTask.priority,
          due_date: newTask.due_date,
          yard: newTask.selected_yard || ''
        }, { headers });
        toast.success('Task created!');
      } else {
        // Create a task for each hive
        for (const hive of targetHives) {
          await axios.post(`${API}/crew/tasks`, {
            assigned_crew: newTask.assigned_crew,
            hive_id: hive.id,
            hive_number: hive.hive_number,
            yard: hive.yard,
            title: targetHives.length > 1 
              ? `${newTask.title} - Hive #${hive.hive_number}`
              : newTask.title,
            description: newTask.description,
            task_type: newTask.task_type,
            priority: newTask.priority,
            due_date: newTask.due_date
          }, { headers });
        }
        toast.success(`${targetHives.length} task(s) created!`);
      }
      
      setShowTaskDialog(false);
      setNewTask({ 
        assigned_crew: [], 
        selected_yard: '', 
        hive_selection: 'single', 
        hive_id: '', 
        hive_number: 0, 
        yard: '', 
        task_type: '', 
        title: '', 
        description: '', 
        priority: 'normal', 
        due_date: '' 
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/crew/${selectedMember.id}/password`, { password: newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Password set for ${selectedMember.name}`);
      setShowPasswordDialog(false);
      setNewPassword('');
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to set password');
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Delete crew member "${member.name}"? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/beekeepers/${member.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Crew member deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete crew member');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus, taskTitle) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/crew/tasks/${taskId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (newStatus === 'completed') {
        toast.success(`"${taskTitle}" marked as completed`);
      } else if (newStatus === 'in_progress') {
        toast.success(`"${taskTitle}" started`);
      } else {
        toast.success(`"${taskTitle}" updated to ${newStatus}`);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTaskIds.size === filteredTasks.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(filteredTasks.map(t => t.id)));
    }
  };

  const handleBulkAction = async (newStatus) => {
    if (selectedTaskIds.size === 0) return;
    setBulkLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const ids = [...selectedTaskIds];
      
      await Promise.all(
        ids.map(id => axios.put(`${API}/crew/tasks/${id}`, { status: newStatus }, { headers }))
      );
      
      const label = newStatus === 'completed' ? 'completed' : 'started';
      toast.success(`${ids.length} task${ids.length > 1 ? 's' : ''} ${label}`);
      setSelectedTaskIds(new Set());
      fetchData();
    } catch (error) {
      toast.error('Failed to update some tasks');
    } finally {
      setBulkLoading(false);
    }
  };

  const handlePrintTaskList = async () => {
    setPrintLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/crew/task-list-pdf`, 
        { crew_member_id: printCrewFilter },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `crew_task_list_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Task list PDF downloaded');
      setShowPrintDialog(false);
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setPrintLoading(false);
    }
  };

  const openEditMember = (member) => {
    setEditMember({ ...member });
    setEditPhotoFile(null);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editMember) return;
    setEditSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editPhotoFile) {
        const fd = new FormData();
        fd.append('file', editPhotoFile);
        await axios.post(`${API}/crew/members/${editMember.id}/photo`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      }
      await axios.put(`${API}/crew/members/${editMember.id}`, {
        name: editMember.name,
        email: editMember.email,
        phone: editMember.phone,
      }, { headers });
      toast.success('Crew member updated');
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setEditSaving(false);
  };

  const handleAssignTask = async () => {
    if (!assignTaskId || assignCrewIds.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/crew/tasks/${assignTaskId}/assign`, { assigned_crew: assignCrewIds }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Crew assigned to task');
      setShowAssignDialog(false);
      setAssignTaskId(null);
      setAssignCrewIds([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleSendWeeklyReport = async () => {
    setSendingReport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/crew/weekly-report/email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Weekly report sent!');
      // Refresh history after sending
      const historyRes = await axios.get(`${API}/crew/reports/history`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setReportHistory(historyRes.data || []);
    } catch (err) {
      toast.error('Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  const handleToggleAutoEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      const newValue = !autoEmailEnabled;
      await axios.put(`${API}/auth/weekly-report-preference`, 
        { weekly_report_auto_email: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutoEmailEnabled(newValue);
      toast.success(newValue ? 'Auto Monday emails enabled' : 'Auto Monday emails disabled');
    } catch (err) {
      toast.error('Failed to update preference');
    }
  };

  const getTaskCountForMember = (memberId) => {
    return tasks.filter(t => t.assigned_to === memberId && t.status !== 'completed').length;
  };

  // Apply filters to tasks
  const filteredTasks = useMemo(() => {
    let result = tasks;
    
    if (filterPriority !== 'all') {
      result = result.filter(t => t.priority === filterPriority);
    }
    
    if (filterDue !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(today);
      endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
      
      result = result.filter(t => {
        if (filterDue === 'no_date') return !t.due_date;
        if (!t.due_date) return false;
        const due = new Date(t.due_date);
        due.setHours(0, 0, 0, 0);
        if (filterDue === 'overdue') return due < today;
        if (filterDue === 'today') return due.getTime() === today.getTime();
        if (filterDue === 'week') return due >= today && due <= endOfWeek;
        return true;
      });
    }
    
    return result;
  }, [tasks, filterPriority, filterDue]);

  // Keep selectedTaskIds in sync: remove any IDs not in filteredTasks
  useEffect(() => {
    setSelectedTaskIds(prev => {
      const validIds = new Set(filteredTasks.map(t => t.id));
      const cleaned = new Set([...prev].filter(id => validIds.has(id)));
      if (cleaned.size !== prev.size) return cleaned;
      return prev;
    });
  }, [filteredTasks]);

  const activeFilterCount = (filterPriority !== 'all' ? 1 : 0) + (filterDue !== 'all' ? 1 : 0);

  // Compute grouped tasks
  const groupedTasks = useMemo(() => {
    if (groupBy === 'none') return null;
    
    const groups = {};
    filteredTasks.forEach(task => {
      if (groupBy === 'crew') {
        // Group by each assigned crew member
        const crewNames = task.assigned_crew && task.assigned_crew.length > 0
          ? task.assigned_crew.map(c => c.name)
          : [task.assigned_to_name || 'Unassigned'];
        crewNames.forEach(name => {
          const key = name || 'Unassigned';
          if (!groups[key]) groups[key] = [];
          groups[key].push(task);
        });
      } else if (groupBy === 'yard') {
        const key = task.yard || 'No Yard Assigned';
        if (!groups[key]) groups[key] = [];
        groups[key].push(task);
      }
    });
    
    // Sort group keys alphabetically, but push 'Unassigned'/'No Yard Assigned' to end
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unassigned' || a === 'No Yard Assigned') return 1;
      if (b === 'Unassigned' || b === 'No Yard Assigned') return -1;
      return a.localeCompare(b);
    });
    
    return sortedKeys.map(key => ({ label: key, tasks: groups[key] }));
  }, [filteredTasks, groupBy]);

  const TASK_TYPES = [
    { value: 'inspection', label: 'Inspection' },
    { value: 'feeding', label: 'Feeding' },
    { value: 'treatment', label: 'Treatment' },
    { value: 'harvest', label: 'Harvest' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'other', label: 'Other' }
  ];

  const PRIORITIES = [
    { value: 'low', label: 'Low', color: 'bg-stone-100 text-stone-700' },
    { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
  ];

  return (
    <div className="bg-stone-50" style={{ width: '100%' }}>
      <div className="hidden lg:block fixed top-0 left-0 h-screen z-40" style={{ width: '224px' }}>
        <Sidebar 
          onLogout={logout}
          isAdmin={user?.is_admin}
          hasFeature={hasFeature}
        />
      </div>
      
      <div className="lg:ml-56">
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                <Users className="h-6 w-6 text-amber-600" />
                Beekeeper Crew
              </h1>
              <p className="text-stone-600 mt-1">Manage your team and assign tasks</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowTaskDialog(true)}
                variant="outline"
                className="border-amber-300"
                disabled={crewMembers.length === 0}
              >
                <ClipboardList className="h-4 w-4 mr-2" />
                Assign Task
              </Button>
              <Button onClick={async () => {
                const check = await checkCanAdd('crew');
                if (check.allowed) setShowAddDialog(true);
              }} className="bg-amber-500 hover:bg-amber-600 text-black">
                <Plus className="h-4 w-4 mr-2" />
                Add Crew Member
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Crew Members Section */}
              <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
                <h2 className="text-xl font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-amber-600" />
                  Crew Members ({crewMembers.length})
                </h2>
                
                {crewMembers.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                    <p>No crew members yet</p>
                    <p className="text-sm mt-1">Add your first crew member to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {crewMembers.map(member => (
                      <div 
                        key={member.id}
                        className="border border-stone-200 rounded-xl p-4 hover:border-amber-300 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3 min-w-0">
                            {member.photo ? (
                              <img src={`${API}/files/${member.photo}`} alt={member.name} className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-lg">
                                {member.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold text-stone-800">{member.name}</div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-stone-500 break-all">
                                {member.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3.5 w-3.5" />
                                    {member.email}
                                  </span>
                                )}
                                {member.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3.5 w-3.5" />
                                    {member.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {member.can_login ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                No Login
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
                          <div className="flex items-center gap-2 text-sm text-stone-600">
                            <ClipboardList className="h-4 w-4" />
                            {getTaskCountForMember(member.id)} active tasks
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditMember(member)}
                              className="h-8 px-2"
                              title="Edit Crew Member"
                              data-testid={`edit-crew-${member.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedMember(member); setShowPasswordDialog(true); }}
                              className="h-8 px-2"
                              title="Set/Reset Password"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMember(member)}
                              className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tasks Section */}
              <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                  <h2 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-amber-600" />
                    Active Tasks ({filteredTasks.length}{activeFilterCount > 0 ? ` of ${tasks.length}` : ''})
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Print Task List */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPrintDialog(true)}
                      className="text-stone-600"
                      data-testid="print-task-list-btn"
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Print
                    </Button>
                    {/* Group By Toggle */}
                    <Select value={groupBy} onValueChange={setGroupBy}>
                      <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="group-by-select">
                        <Layers className="h-3.5 w-3.5 mr-1 text-stone-500" />
                        <SelectValue placeholder="Group by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Grouping</SelectItem>
                        <SelectItem value="crew">By Crew</SelectItem>
                        <SelectItem value="yard">By Yard</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowHistoryDialog(true)}
                      className="text-stone-600"
                    >
                      <History className="h-4 w-4 mr-1" />
                      History ({completedTasks.length})
                    </Button>
                  </div>
                </div>
                
                {/* Filter Bar */}
                {tasks.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-stone-100" data-testid="task-filter-bar">
                    <Filter className="h-3.5 w-3.5 text-stone-400" />
                    {/* Priority Filter */}
                    <Select value={filterPriority} onValueChange={setFilterPriority}>
                      <SelectTrigger className="w-[120px] h-7 text-xs" data-testid="filter-priority-select">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Due Date Filter */}
                    <Select value={filterDue} onValueChange={setFilterDue}>
                      <SelectTrigger className="w-[120px] h-7 text-xs" data-testid="filter-due-select">
                        <SelectValue placeholder="Due date" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dates</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                        <SelectItem value="today">Due Today</SelectItem>
                        <SelectItem value="week">This Week</SelectItem>
                        <SelectItem value="no_date">No Due Date</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="h-4 w-px bg-stone-200 mx-1" />
                    {/* Select All */}
                    <button
                      onClick={toggleSelectAll}
                      className="flex items-center gap-1.5 text-xs text-stone-600 hover:text-stone-800 transition-colors"
                      data-testid="select-all-tasks"
                    >
                      {selectedTaskIds.size === filteredTasks.length && filteredTasks.length > 0
                        ? <CheckSquare className="h-3.5 w-3.5 text-amber-600" />
                        : <Square className="h-3.5 w-3.5" />}
                      {selectedTaskIds.size > 0 ? `${selectedTaskIds.size} selected` : 'Select all'}
                    </button>
                    {activeFilterCount > 0 && (
                      <button
                        onClick={() => { setFilterPriority('all'); setFilterDue('all'); }}
                        className="text-xs text-amber-600 hover:text-amber-800 font-medium ml-1"
                        data-testid="clear-filters-btn"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
                {/* Bulk Action Bar */}
                {selectedTaskIds.size > 0 && (
                  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-3 animate-in slide-in-from-top-2" data-testid="bulk-action-bar">
                    <span className="text-sm font-medium text-amber-800">
                      {selectedTaskIds.size} task{selectedTaskIds.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleBulkAction('in_progress')}
                        disabled={bulkLoading}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors disabled:opacity-50"
                        data-testid="bulk-start-btn"
                      >
                        <Play className="h-3 w-3" /> Start All
                      </button>
                      <button
                        onClick={() => handleBulkAction('completed')}
                        disabled={bulkLoading}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
                        data-testid="bulk-complete-btn"
                      >
                        <CheckCircle className="h-3 w-3" /> Complete All
                      </button>
                      <button
                        onClick={() => setSelectedTaskIds(new Set())}
                        className="text-xs text-stone-500 hover:text-stone-700 ml-1"
                        data-testid="bulk-cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {tasks.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                    <p>No active tasks</p>
                    <p className="text-sm mt-1">Assign tasks to your crew members</p>
                  </div>
                ) : filteredTasks.length === 0 ? (
                  <div className="text-center py-6 text-stone-500">
                    <Filter className="h-10 w-10 mx-auto mb-2 text-stone-300" />
                    <p className="text-sm">No tasks match your filters</p>
                    <button
                      onClick={() => { setFilterPriority('all'); setFilterDue('all'); }}
                      className="text-xs text-amber-600 hover:text-amber-800 font-medium mt-1"
                    >
                      Clear all filters
                    </button>
                  </div>
                ) : groupBy !== 'none' && groupedTasks ? (
                  /* Grouped rendering */
                  <div className="space-y-4 max-h-[500px] overflow-y-auto" data-testid="grouped-tasks-container">
                    {groupedTasks.map(group => (
                      <div key={group.label} data-testid={`task-group-${group.label.replace(/\s+/g, '-').toLowerCase()}`}>
                        <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10">
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                            groupBy === 'crew' 
                              ? 'bg-indigo-100 text-indigo-700' 
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {groupBy === 'crew' ? <Users className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                            {group.label}
                          </div>
                          <span className="text-xs text-stone-400">{group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-2 ml-2 pl-3 border-l-2 border-stone-100">
                          {group.tasks.map(task => {
                            const priorityStyle = PRIORITIES.find(p => p.value === task.priority);
                            const allCrewNames = task.assigned_crew && task.assigned_crew.length > 0
                              ? task.assigned_crew.map(c => c.name)
                              : [task.assigned_to_name].filter(Boolean);
                            
                            return (
                              <div 
                                key={`${group.label}-${task.id}`}
                                className={`border rounded-lg p-3 transition-colors ${
                                  selectedTaskIds.has(task.id) 
                                    ? 'border-amber-400 bg-amber-50/50' 
                                    : 'border-stone-200 hover:border-amber-300'
                                }`}
                                data-testid={`task-card-${task.id}`}
                              >
                                <div className="flex items-start gap-2">
                                  <button
                                    onClick={() => toggleTaskSelection(task.id)}
                                    className="mt-0.5 flex-shrink-0"
                                    data-testid={`task-select-${task.id}`}
                                  >
                                    {selectedTaskIds.has(task.id)
                                      ? <CheckSquare className="h-4 w-4 text-amber-600" />
                                      : <Square className="h-4 w-4 text-stone-300 hover:text-stone-500" />}
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="font-medium text-stone-800">{task.title}</div>
                                        {groupBy === 'yard' && (
                                          <div className="text-sm text-stone-500 mt-1 flex flex-wrap items-center gap-1">
                                            <span>Assigned to:</span>
                                            {allCrewNames.length === 0 ? (
                                              <button
                                                onClick={(e) => { e.stopPropagation(); setAssignTaskId(task.id); setAssignCrewIds([]); setShowAssignDialog(true); }}
                                                className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium transition"
                                                data-testid={`assign-task-${task.id}`}
                                              >
                                                Unassigned — Assign
                                              </button>
                                            ) : (
                                              <span className="font-medium">
                                                {allCrewNames.length <= 2 
                                                  ? allCrewNames.join(', ') 
                                                  : `${allCrewNames.slice(0, 2).join(', ')} +${allCrewNames.length - 2} more`}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        {groupBy === 'crew' && task.yard && (
                                          <div className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                                            <MapPin className="h-3.5 w-3.5" />
                                            {task.yard}
                                            {task.hive_number > 0 && (
                                              <span className="text-stone-500">• Hive #{task.hive_number}</span>
                                            )}
                                          </div>
                                        )}
                                        {groupBy === 'yard' && task.hive_number > 0 && (
                                          <div className="text-sm text-stone-500 mt-1">
                                            Hive #{task.hive_number}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-end gap-1">
                                        <span className={`px-2 py-0.5 text-xs rounded-full ${priorityStyle?.color || 'bg-stone-100'}`}>
                                          {task.priority}
                                        </span>
                                        {task.due_date && (
                                          <span className="text-xs text-stone-500">
                                            Due: {new Date(task.due_date).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {/* Status Actions */}
                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-100">
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-500'
                                      }`}>
                                        {task.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                      </span>
                                      <div className="flex gap-1.5">
                                        {task.status !== 'in_progress' && (
                                          <button
                                            onClick={() => handleUpdateTaskStatus(task.id, 'in_progress', task.title)}
                                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                            data-testid={`task-start-${task.id}`}
                                          >
                                            <Play className="h-3 w-3" /> Start
                                          </button>
                                        )}
                                        <button
                                          onClick={() => handleUpdateTaskStatus(task.id, 'completed', task.title)}
                                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                          data-testid={`task-complete-${task.id}`}
                                        >
                                          <CheckCircle className="h-3 w-3" /> Complete
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Flat/ungrouped rendering */
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {filteredTasks.map(task => {
                      const priorityStyle = PRIORITIES.find(p => p.value === task.priority);
                      // Get all assigned crew names
                      const allCrewNames = task.assigned_crew && task.assigned_crew.length > 0
                        ? task.assigned_crew.map(c => c.name)
                        : [task.assigned_to_name].filter(Boolean);
                      
                      return (
                        <div 
                          key={task.id}
                          className={`border rounded-lg p-3 transition-colors ${
                            selectedTaskIds.has(task.id) 
                              ? 'border-amber-400 bg-amber-50/50' 
                              : 'border-stone-200 hover:border-amber-300'
                          }`}
                          data-testid={`task-card-${task.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <button
                              onClick={() => toggleTaskSelection(task.id)}
                              className="mt-0.5 flex-shrink-0"
                              data-testid={`task-select-${task.id}`}
                            >
                              {selectedTaskIds.has(task.id)
                                ? <CheckSquare className="h-4 w-4 text-amber-600" />
                                : <Square className="h-4 w-4 text-stone-300 hover:text-stone-500" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-stone-800">{task.title}</div>
                                  <div className="text-sm text-stone-500 mt-1 flex flex-wrap items-center gap-1">
                                    <span>Assigned to:</span>
                                    {allCrewNames.length === 0 ? (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setAssignTaskId(task.id); setAssignCrewIds([]); setShowAssignDialog(true); }}
                                        className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium transition"
                                        data-testid={`assign-task-flat-${task.id}`}
                                      >
                                        Unassigned — Assign
                                      </button>
                                    ) : allCrewNames.length === 1 ? (
                                      <span className="font-medium">{allCrewNames[0]}</span>
                                    ) : (
                                      <span className="font-medium">
                                        {allCrewNames.slice(0, 2).join(', ')}
                                        {allCrewNames.length > 2 && (
                                          <span className="text-indigo-600 ml-1">+{allCrewNames.length - 2} more</span>
                                        )}
                                      </span>
                                    )}
                                    {allCrewNames.length > 1 && (
                                      <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded text-xs ml-1">
                                        {allCrewNames.length} crew
                                      </span>
                                    )}
                                  </div>
                                  {task.yard && (
                                    <div className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                                      <MapPin className="h-3.5 w-3.5" />
                                      {task.yard}
                                      {task.hive_number > 0 && (
                                        <span className="text-stone-500">• Hive #{task.hive_number}</span>
                                      )}
                                    </div>
                                  )}
                                  {!task.yard && task.hive_number > 0 && (
                                    <div className="text-sm text-stone-500 mt-1">
                                      Hive #{task.hive_number}
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${priorityStyle?.color || 'bg-stone-100'}`}>
                                    {task.priority}
                                  </span>
                                  {task.due_date && (
                                    <span className="text-xs text-stone-500">
                                      Due: {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {task.notes && task.notes.length > 0 && (
                                <div className="mt-2 flex items-center gap-1 text-xs text-stone-500">
                                  <MessageSquare className="h-3 w-3" />
                                  {task.notes.length} note(s)
                                </div>
                              )}
                              {/* Comments Toggle */}
                              <button
                                onClick={() => setExpandedComments(prev => ({ ...prev, [task.id]: !prev[task.id] }))}
                                className="mt-2 flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
                                data-testid={`toggle-comments-${task.id}`}
                              >
                                <MessageSquare className="h-3 w-3" />
                                Comments
                                {expandedComments[task.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              </button>
                              {expandedComments[task.id] && (
                                <div className="mt-2 border border-stone-200 rounded-lg overflow-hidden">
                                  <TaskComments taskId={task.id} taskTitle={task.title} />
                                </div>
                              )}
                              {/* Status Actions */}
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-100">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-500'
                                }`}>
                                  {task.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                </span>
                                <div className="flex gap-1.5">
                                  {task.status !== 'in_progress' && (
                                    <button
                                      onClick={() => handleUpdateTaskStatus(task.id, 'in_progress', task.title)}
                                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                      data-testid={`task-start-${task.id}`}
                                    >
                                      <Play className="h-3 w-3" /> Start
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleUpdateTaskStatus(task.id, 'completed', task.title)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                    data-testid={`task-complete-${task.id}`}
                                  >
                                    <CheckCircle className="h-3 w-3" /> Complete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Weekly Crew Report Card */}
          {weeklyReport && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 overflow-hidden mt-6" data-testid="weekly-report-card">
              <div className="bg-gradient-to-r from-amber-700 to-amber-600 px-5 py-3 flex items-center justify-between">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Weekly Crew Report
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-amber-200 text-xs">{weeklyReport.period}</span>
                  <button
                    onClick={handleSendWeeklyReport}
                    disabled={sendingReport}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-white/20 text-white hover:bg-white/30 transition-colors disabled:opacity-50"
                    data-testid="send-report-email-btn"
                  >
                    <Send className="h-3 w-3" />
                    {sendingReport ? 'Sending...' : 'Email Report'}
                  </button>
                </div>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-800">{weeklyReport.total_tasks_completed}</div>
                    <div className="text-xs text-amber-600">Tasks Done</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-700">{weeklyReport.total_field_hours}h</div>
                    <div className="text-xs text-green-600">Field Hours</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-700">{weeklyReport.total_yard_visits}</div>
                    <div className="text-xs text-blue-600">Yard Visits</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-700">{weeklyReport.unique_yards}</div>
                    <div className="text-xs text-purple-600">Unique Yards</div>
                  </div>
                </div>

                {weeklyReport.top_performer && (
                  <div className="bg-white rounded-lg border border-amber-200 p-3 mb-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Trophy className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-xs text-amber-600 font-medium">Top Performer</div>
                      <div className="font-semibold text-stone-800">{weeklyReport.top_performer.name}</div>
                      <div className="text-xs text-stone-500">
                        {weeklyReport.top_performer.tasks_completed} tasks, {weeklyReport.top_performer.total_hours}h, {weeklyReport.top_performer.visits} visits
                      </div>
                    </div>
                  </div>
                )}

                {weeklyReport.crew_rankings && weeklyReport.crew_rankings.length > 0 && (
                  <div className="space-y-2">
                    {weeklyReport.crew_rankings.map((crew, idx) => (
                      <div key={crew.name} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2 border border-stone-100">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${
                            idx === 0 ? 'bg-amber-500 text-white' : 'bg-stone-200 text-stone-600'
                          }`}>{idx + 1}</span>
                          <span className="font-medium text-stone-700">{crew.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-stone-500">
                          <span>{crew.tasks_completed} tasks</span>
                          <span>{crew.total_hours}h</span>
                          <span>{crew.visits} visits</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {weeklyReport.crew_rankings && weeklyReport.crew_rankings.length === 0 && (
                  <div className="text-center text-sm text-stone-400 py-4">
                    No crew activity this week
                  </div>
                )}
                
                {/* Auto Email Toggle & History Link */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-amber-200">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-email-toggle">
                    <div 
                      onClick={handleToggleAutoEmail}
                      className={`relative w-10 h-5 rounded-full transition-colors ${autoEmailEnabled ? 'bg-amber-500' : 'bg-stone-300'}`}
                    >
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${autoEmailEnabled ? 'translate-x-5' : ''}`} />
                    </div>
                    <span className="text-xs text-stone-600">Auto-send every Monday</span>
                  </label>
                  <button
                    onClick={() => setShowReportHistory(!showReportHistory)}
                    className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium"
                    data-testid="view-report-history-btn"
                  >
                    <History className="h-3 w-3" />
                    {showReportHistory ? 'Hide History' : `Past Reports${reportHistory.length > 0 ? ` (${reportHistory.length})` : ''}`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Report History Section */}
          {showReportHistory && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mt-4" data-testid="report-history-section">
              <div className="bg-stone-50 px-5 py-3 border-b border-stone-200 flex items-center justify-between">
                <h3 className="font-semibold text-stone-800 flex items-center gap-2">
                  <History className="h-4 w-4 text-stone-500" />
                  Report History
                </h3>
                <span className="text-xs text-stone-400">{reportHistory.length} report{reportHistory.length !== 1 ? 's' : ''}</span>
              </div>
              {reportHistory.length === 0 ? (
                <div className="text-center text-sm text-stone-400 py-8">
                  No reports sent yet. Send your first report above.
                </div>
              ) : (
                <div className="divide-y divide-stone-100 max-h-96 overflow-y-auto">
                  {reportHistory.map((report) => (
                    <div 
                      key={report.id} 
                      className="px-5 py-3 hover:bg-stone-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedHistoryReport(selectedHistoryReport?.id === report.id ? null : report)}
                      data-testid={`report-history-item-${report.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-stone-800">{report.period}</div>
                          <div className="text-xs text-stone-400 flex items-center gap-2 mt-0.5">
                            <span>{report.auto_sent ? 'Auto-sent' : 'Manual'}</span>
                            <span>to {report.sent_to}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-stone-500">
                          <span className="font-medium text-amber-700">{report.total_tasks_completed} tasks</span>
                          <span className="font-medium text-green-700">{report.total_field_hours}h</span>
                          <span className="font-medium text-blue-700">{report.total_yard_visits} visits</span>
                        </div>
                      </div>
                      {selectedHistoryReport?.id === report.id && report.crew_rankings && (
                        <div className="mt-3 space-y-1.5 pl-2 border-l-2 border-amber-200">
                          {report.crew_rankings.map((crew, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs">
                              <span className="text-stone-600 font-medium">{crew.name}</span>
                              <span className="text-stone-400">
                                {crew.tasks} tasks, {crew.hours}h, {crew.visits} visits
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Crew Time Metrics */}
          {timeMetrics && timeMetrics.total_visits > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mt-6" data-testid="time-metrics-section">
              <h2 className="text-xl font-bold text-stone-800 mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Crew Productivity (Last 30 Days)
              </h2>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{timeMetrics.total_visits}</div>
                  <div className="text-xs text-blue-600">Total Visits</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{timeMetrics.total_hours}h</div>
                  <div className="text-xs text-green-600">Total Hours</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{timeMetrics.avg_visit_minutes}m</div>
                  <div className="text-xs text-amber-600">Avg per Visit</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-700">{Object.keys(timeMetrics.by_yard).length}</div>
                  <div className="text-xs text-purple-600">Yards Visited</div>
                </div>
              </div>
              
              {/* Time by Crew Member */}
              {Object.keys(timeMetrics.by_crew).length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-stone-700 mb-2">Time by Crew Member</h3>
                  <div className="space-y-2">
                    {Object.entries(timeMetrics.by_crew).sort((a, b) => b[1].total_minutes - a[1].total_minutes).map(([name, data]) => {
                      const maxMins = Math.max(...Object.values(timeMetrics.by_crew).map(d => d.total_minutes));
                      const pct = maxMins > 0 ? (data.total_minutes / maxMins) * 100 : 0;
                      const hrs = Math.floor(data.total_minutes / 60);
                      const mins = Math.round(data.total_minutes % 60);
                      return (
                        <div key={name} className="text-sm" data-testid={`crew-metric-${name}`}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium text-stone-700">{name}</span>
                            <span className="text-stone-500 text-xs">
                              {hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`} — {data.visits} visit{data.visits !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Time by Yard */}
              {Object.keys(timeMetrics.by_yard).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-stone-700 mb-2">Time by Yard</h3>
                  <div className="space-y-2">
                    {Object.entries(timeMetrics.by_yard).sort((a, b) => b[1].total_minutes - a[1].total_minutes).map(([yard, data]) => {
                      const maxMins = Math.max(...Object.values(timeMetrics.by_yard).map(d => d.total_minutes));
                      const pct = maxMins > 0 ? (data.total_minutes / maxMins) * 100 : 0;
                      const hrs = Math.floor(data.total_minutes / 60);
                      const mins = Math.round(data.total_minutes % 60);
                      return (
                        <div key={yard} className="text-sm" data-testid={`yard-metric-${yard}`}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium text-stone-700 flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-emerald-500" /> {yard}
                            </span>
                            <span className="text-stone-500 text-xs">
                              {hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`} avg {data.avg_minutes}m — {data.visits} visit{data.visits !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Crew Activity Timeline */}
          {crewCheckins.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mt-6">
              <h2 className="text-xl font-bold text-stone-800 mb-4 flex items-center gap-2">
                <MapPinCheck className="h-5 w-5 text-green-600" />
                Field Activity (Last 7 Days)
              </h2>
              <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="activity-timeline">
                {crewCheckins.map(checkin => (
                  <div key={checkin.id} className="flex items-start gap-3 text-sm" data-testid={`checkin-${checkin.id}`}>
                    <div className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      checkin.action === 'check_in' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {checkin.action === 'check_in' 
                        ? <LogIn className="h-4 w-4 text-green-600" />
                        : <LogOutIcon className="h-4 w-4 text-red-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-800">
                        {checkin.crew_name} — {checkin.action === 'check_in' ? 'Checked In' : 'Checked Out'}
                      </div>
                      <div className="text-stone-500 flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" />
                        {checkin.yard_name}
                        {checkin.accuracy && (
                          <span className="text-stone-400 text-xs">({checkin.accuracy}m accuracy)</span>
                        )}
                        {checkin.duration_minutes && (
                          <span className="text-blue-600 text-xs font-medium">
                            ({checkin.duration_minutes >= 60 
                              ? `${Math.floor(checkin.duration_minutes / 60)}h ${Math.round(checkin.duration_minutes % 60)}m` 
                              : `${Math.round(checkin.duration_minutes)}m`})
                          </span>
                        )}
                      </div>
                      {checkin.latitude && checkin.longitude && (
                        <div className="text-stone-400 text-xs mt-0.5">
                          GPS: {Number(checkin.latitude).toFixed(5)}, {Number(checkin.longitude).toFixed(5)}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-stone-400 text-right flex-shrink-0">
                      <div>{new Date(checkin.created_at).toLocaleDateString()}</div>
                      <div>{new Date(checkin.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Crew Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-600" />
              Add Crew Member
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newMember.name}
                onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                placeholder="crew@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={newMember.phone}
                onChange={(e) => setNewMember({...newMember, phone: e.target.value})}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label>Experience Level</Label>
              <Select value={newMember.experience_level} onValueChange={(v) => setNewMember({...newMember, experience_level: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="experienced">Experienced</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Password (for crew login)</Label>
              <Input
                type="password"
                value={newMember.password}
                onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                placeholder="Set password (min 6 characters)"
              />
              <p className="text-xs text-amber-600 mt-1 font-medium">⚠ Minimum 6 characters required</p>
              <p className="text-xs text-stone-500">Leave blank to set later</p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddMember} className="bg-amber-500 hover:bg-amber-600 text-black">
              Add Crew Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              Assign Task
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Multiple Crew Selection */}
            <div className="space-y-2">
              <Label>Assign To * (select multiple)</Label>
              <div className="border border-stone-200 rounded-lg p-3 max-h-[150px] overflow-y-auto">
                {crewMembers.length === 0 ? (
                  <p className="text-sm text-stone-500">No crew members. Add crew members first.</p>
                ) : (
                  <div className="space-y-2">
                    {crewMembers.map(m => (
                      <label key={m.id} className="flex items-center gap-2 cursor-pointer hover:bg-stone-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={newTask.assigned_crew.includes(m.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTask({...newTask, assigned_crew: [...newTask.assigned_crew, m.id]});
                            } else {
                              setNewTask({...newTask, assigned_crew: newTask.assigned_crew.filter(id => id !== m.id)});
                            }
                          }}
                          className="w-4 h-4 rounded border-stone-300 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm">{m.name}</span>
                        {m.email && <span className="text-xs text-stone-400">({m.email})</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {newTask.assigned_crew.length > 0 && (
                <p className="text-xs text-stone-500">{newTask.assigned_crew.length} crew member(s) selected</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Task Title *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                placeholder="e.g., Inspect Hives, Check Feeders"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Task Type</Label>
              <Select value={newTask.task_type} onValueChange={(v) => setNewTask({...newTask, task_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Yard Selection */}
            <div className="space-y-2">
              <Label>Apiary/Yard</Label>
              <Select 
                value={newTask.selected_yard || "none"} 
                onValueChange={(v) => setNewTask({
                  ...newTask, 
                  selected_yard: v === "none" ? '' : v, 
                  hive_id: '',
                  hive_selection: 'single'
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select apiary (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific apiary</SelectItem>
                  {yards.map(y => (
                    <SelectItem key={y.id} value={y.name}>
                      {y.name} ({hives.filter(h => h.yard === y.name).length} hives)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Hive Selection - shows when yard is selected */}
            {newTask.selected_yard && (
              <div className="space-y-2">
                <Label>Hive(s)</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewTask({...newTask, hive_selection: 'all', hive_id: ''})}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        newTask.hive_selection === 'all' 
                          ? 'bg-amber-500 text-black border-amber-500' 
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                    >
                      All Hives ({hives.filter(h => h.yard === newTask.selected_yard).length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewTask({...newTask, hive_selection: 'single'})}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        newTask.hive_selection === 'single' 
                          ? 'bg-amber-500 text-black border-amber-500' 
                          : 'border-stone-200 hover:border-amber-300'
                      }`}
                    >
                      Select Hive
                    </button>
                  </div>
                  
                  {newTask.hive_selection === 'single' && (
                    <Select value={newTask.hive_id} onValueChange={(v) => setNewTask({...newTask, hive_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select hive" />
                      </SelectTrigger>
                      <SelectContent>
                        {hives.filter(h => h.yard === newTask.selected_yard).map(h => (
                          <SelectItem key={h.id} value={h.id}>
                            Hive {h.hive_number} {h.nickname && `"${h.nickname}"`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {newTask.hive_selection === 'all' && (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      Tasks will be created for all {hives.filter(h => h.yard === newTask.selected_yard).length} hives in {newTask.selected_yard}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                placeholder="Task details..."
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} className="bg-amber-500 hover:bg-amber-600 text-black">
              Create Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-600" />
              Set Password for {selectedMember?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="mt-2"
            />
            <p className="text-xs text-amber-600 mt-1.5 font-medium">⚠ Minimum 6 characters required</p>
            <p className="text-xs text-stone-500 mt-1">
              This will allow {selectedMember?.name} to log in using their email and this password.
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setShowPasswordDialog(false); setNewPassword(''); }}>Cancel</Button>
            <Button onClick={handleSetPassword} className="bg-amber-500 hover:bg-amber-600 text-black">
              Set Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Completed Task History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-amber-600" />
              Completed Task History
            </DialogTitle>
          </DialogHeader>
          
          {/* Date Filter */}
          <div className="flex items-center gap-2 py-2 border-b">
            <Filter className="h-4 w-4 text-stone-500" />
            <span className="text-sm text-stone-600">Filter:</span>
            <div className="flex gap-1">
              {[
                { value: 'all', label: 'All Time' },
                { value: '7days', label: '7 Days' },
                { value: '30days', label: '30 Days' },
                { value: '90days', label: '90 Days' }
              ].map(filter => (
                <Button
                  key={filter.value}
                  variant={historyDateFilter === filter.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setHistoryDateFilter(filter.value)}
                  className={historyDateFilter === filter.value ? 'bg-amber-500 hover:bg-amber-600 text-black' : ''}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Task List */}
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {completedTasks
              .filter(task => {
                if (historyDateFilter === 'all') return true;
                const completedDate = new Date(task.completed_date || task.created_at);
                const now = new Date();
                const days = parseInt(historyDateFilter);
                const cutoff = new Date(now.setDate(now.getDate() - days));
                return completedDate >= cutoff;
              })
              .sort((a, b) => new Date(b.completed_date || b.created_at) - new Date(a.completed_date || a.created_at))
              .map(task => (
                <div 
                  key={task.id}
                  className="border border-stone-200 rounded-lg p-4 bg-green-50/50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-stone-800">{task.title}</span>
                      </div>
                      <div className="text-sm text-stone-500 mt-1">
                        Assigned to: <span className="font-medium">{task.assigned_to_name}</span>
                      </div>
                      {task.hive_number > 0 && (
                        <div className="text-sm text-stone-500">
                          Hive #{task.hive_number} {task.yard && `• ${task.yard}`}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-green-700 font-medium">Completed</div>
                      <div className="text-xs text-stone-500">
                        {task.completed_date 
                          ? new Date(task.completed_date).toLocaleDateString()
                          : 'Date unknown'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes Section */}
                  {task.notes && task.notes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-stone-200">
                      <div className="text-xs font-medium text-stone-600 mb-2 flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        Notes ({task.notes.length})
                      </div>
                      <div className="space-y-2">
                        {task.notes.map((note, idx) => (
                          <div key={idx} className="text-sm bg-white rounded p-2 border border-stone-100">
                            <div className="flex justify-between text-xs text-stone-500 mb-1">
                              <span className="font-medium">{note.author_name}</span>
                              <span>{new Date(note.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-stone-700">{note.message || note.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            
            {completedTasks.filter(task => {
              if (historyDateFilter === 'all') return true;
              const completedDate = new Date(task.completed_date || task.created_at);
              const now = new Date();
              const days = parseInt(historyDateFilter);
              const cutoff = new Date(now.setDate(now.getDate() - days));
              return completedDate >= cutoff;
            }).length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <History className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                <p>No completed tasks in this period</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Task List Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-amber-600" />
              Print Task List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-500">
              Generate a printable PDF with yard map and task checklist.
            </p>
            <div>
              <Label className="text-sm font-medium">Filter by Crew Member</Label>
              <Select value={printCrewFilter} onValueChange={setPrintCrewFilter}>
                <SelectTrigger className="mt-1.5" data-testid="print-crew-filter">
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Crew Members</SelectItem>
                  {crewMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-600 space-y-1">
              <div className="font-medium text-stone-700">PDF will include:</div>
              <div>• Yard locations map (where coordinates are available)</div>
              <div>• Task checklist grouped by yard</div>
              <div>• Priority indicators and due dates</div>
              <div>• Notes section for field use</div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowPrintDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePrintTaskList} 
                disabled={printLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="print-download-btn"
              >
                {printLoading ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Crew Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Crew Member</DialogTitle>
          </DialogHeader>
          {editMember && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-4">
                {editMember.photo ? (
                  <img src={`${API}/files/${editMember.photo}`} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-2xl">
                    {(editMember.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" className="hidden" id="crew-photo-upload"
                    onChange={e => { if (e.target.files[0]) setEditPhotoFile(e.target.files[0]); }} />
                  <label htmlFor="crew-photo-upload" className="cursor-pointer text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 px-3 py-1.5 rounded-full font-medium transition">
                    {editPhotoFile ? editPhotoFile.name : 'Upload Photo'}
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-stone-700 font-medium">Name</Label>
                <Input value={editMember.name || ''} onChange={e => setEditMember(prev => ({ ...prev, name: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-stone-700 font-medium">Email</Label>
                <Input value={editMember.email || ''} onChange={e => setEditMember(prev => ({ ...prev, email: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-stone-700 font-medium">Phone</Label>
                <Input value={editMember.phone || ''} onChange={e => setEditMember(prev => ({ ...prev, phone: e.target.value }))} className="mt-1" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={editSaving} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black">
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Crew to Task Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign Crew Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {crewMembers.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-4">No crew members available. Add a crew member first.</p>
            ) : crewMembers.map(cm => (
              <button
                key={cm.id}
                onClick={() => setAssignCrewIds(prev => prev.includes(cm.id) ? prev.filter(x => x !== cm.id) : [...prev, cm.id])}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition text-left ${
                  assignCrewIds.includes(cm.id) ? 'border-amber-400 bg-amber-50' : 'border-stone-200 hover:border-stone-300'
                }`}
                data-testid={`assign-crew-option-${cm.id}`}
              >
                {cm.photo ? (
                  <img src={`${API}/files/${cm.photo}`} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm">
                    {cm.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-medium text-stone-800 text-sm">{cm.name}</div>
                  {cm.email && <div className="text-xs text-stone-400">{cm.email}</div>}
                </div>
                {assignCrewIds.includes(cm.id) && <CheckCircle className="h-5 w-5 text-amber-600" />}
              </button>
            ))}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAssignTask} disabled={assignCrewIds.length === 0} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black">
                Assign ({assignCrewIds.length})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrewManagement;
